/**
 * OrganizationsTable Component
 *
 * Таблица организаций на основе DataTable.
 * Включает сортировку, поиск и фильтрацию по колонкам.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Badge, Group, Tooltip } from '@mantine/core';
import { IconBuilding } from '@tabler/icons-react';
import DataTable from '../common/DataTable';
import tokens from '../../theme/designTokens';

// Фолбэк конфигурации badge-ей для partnership_status
// Используется если statusesMap не передан через пропс
const FALLBACK_PARTNERSHIP_BADGES = {
  'partner': { color: 'green', label: 'Партнер' },
  'potential_partner': { color: 'blue', label: 'Потенциальный' },
  'previously_cooperated': { color: 'cyan', label: 'Ранее сотрудничали' },
  'low_cost': { color: 'yellow', label: 'Лоу Кост' },
  'high_cost': { color: 'orange', label: 'Хай Кост' },
  'not_interested': { color: 'red', label: 'Не интересен' },
  'gov_schools_gardens': { color: 'gray', label: 'Гос. школы/сады' },
  'gov_children_centers': { color: 'gray', label: 'Гос. детские центры' },
};

// Конфигурация badge-ей для этапов воронки (cooperation_stage)
// Цвета соответствуют ProgressPath на карточке организации
const STAGE_BADGES = {
  'new': { color: '#0176D3', label: 'Новый' },                    // Синий
  'message_sent': { color: '#7DC681', label: 'Сообщ.' },          // Светло-зелёный
  'call': { color: '#5EBA62', label: 'Звонок' },                  // Зелёный 1
  'meeting': { color: '#48A84C', label: 'Встреча' },              // Зелёный 2
  'master_class': { color: '#3D9141', label: 'МК' },              // Зелёный 3
  'contract_signed': { color: '#2E7D32', label: 'Договор' },      // Зелёный 4
  'classes_active': { color: '#1B5E20', label: 'Занятия' },       // Насыщенный зелёный
  'classes_paused': { color: '#FFA000', label: 'Пауза' },         // Жёлтый/оранжевый
  'contract_terminated': { color: '#C62828', label: 'Растор.' },  // Красный
};

// Фолбэк опций для фильтра статуса партнёрства
const FALLBACK_PARTNERSHIP_FILTER_OPTIONS = [
  { value: 'partner', label: 'Партнер' },
  { value: 'potential_partner', label: 'Потенциальный' },
  { value: 'previously_cooperated', label: 'Ранее сотрудничали' },
  { value: 'low_cost', label: 'Лоу Кост' },
  { value: 'high_cost', label: 'Хай Кост' },
  { value: 'not_interested', label: 'Не интересен' },
  { value: 'gov_schools_gardens', label: 'Гос. школы/сады' },
  { value: 'gov_children_centers', label: 'Гос. детские центры' },
];

// Опции для фильтра этапа воронки
const STAGE_FILTER_OPTIONS = [
  { value: 'new', label: 'Новый' },
  { value: 'message_sent', label: 'Сообщение' },
  { value: 'call', label: 'Звонок' },
  { value: 'meeting', label: 'Встреча' },
  { value: 'master_class', label: 'Мастер-класс' },
  { value: 'contract_signed', label: 'Договор' },
  { value: 'classes_active', label: 'Занятия' },
  { value: 'classes_paused', label: 'Пауза' },
  { value: 'contract_terminated', label: 'Расторгнут' },
];

// Колонки таблицы с конфигурацией фильтрации и поиска
// Принимает statusesMap для динамических статусов и dynamicFilterOptions из API
const getColumns = (statusesMap, partnershipFilterOptions, dynamicFilterOptions) => [
  {
    key: 'name',
    label: 'Название',
    sortable: true,
    filterable: false, // Название фильтруем через поиск, не чекбоксами
    searchable: true,
    // Поиск также по адресу (отображается под названием)
    searchValue: (value, row) => `${value || ''} ${row.address || ''}`,
  },
  {
    key: 'category',
    label: 'Категория',
    sortable: true,
    filterable: true,
    filterOptions: dynamicFilterOptions?.categories?.map(c => ({ value: c, label: c })),
    searchable: true,
  },
  {
    key: 'organization_type',
    label: 'Тип',
    sortable: true,
    filterable: true,
    filterOptions: [
      { value: 'b2b', label: 'B2B' },
      { value: 'b2c', label: 'B2C' },
      { value: 'individual', label: 'Индивидуальные' },
      { value: '__empty__', label: 'Не указан' },
    ],
    searchable: true,
    searchValue: (value) => {
      const labels = { b2b: 'B2B', b2c: 'B2C', individual: 'Индивидуальные' };
      return labels[value] || value || 'Не указан';
    },
  },
  {
    key: 'district_name',
    label: 'Район',
    sortable: true,
    filterable: true,
    filterOptions: dynamicFilterOptions?.districtNames?.map(d => ({ value: d, label: d })),
    searchable: true,
  },
  {
    key: 'company_group_name',
    label: 'Группа',
    sortable: true,
    filterable: true,
    filterOptions: dynamicFilterOptions?.companyGroupNames?.map(g => ({ value: g, label: g })),
    searchable: true,
  },
  {
    key: 'rating',
    label: 'Рейтинг',
    sortable: true,
    sortType: 'number',
    filterable: false,
    searchable: false, // Рейтинг не ищем
  },
  {
    key: 'cooperation_stage',
    label: 'Этап',
    sortable: true,
    filterable: true,
    filterOptions: STAGE_FILTER_OPTIONS,
    searchable: true,
    // Поиск по отображаемому названию этапа
    searchValue: (value) => {
      const badge = STAGE_BADGES[value];
      return badge ? badge.label : value;
    },
  },
  {
    key: 'assigned_to_user_id',
    label: 'Сейл',
    sortable: false,
    filterable: true,
    filterOptions: dynamicFilterOptions?.salesUsers?.map(u => ({
      value: u.id,
      label: u.full_name || u.email
    })),
    searchable: true,
    // Поиск по имени сейла
    searchValue: (value, row) => {
      if (!row.assigned_to) return '';
      return row.assigned_to.full_name || row.assigned_to.email || '';
    },
  },
  {
    key: 'manager_user_id',
    label: 'Менеджер',
    sortable: false,
    filterable: true,
    filterOptions: dynamicFilterOptions?.managerUsers?.map(u => ({
      value: u.id,
      label: u.full_name || u.email
    })),
    searchable: true,
    // Поиск по имени менеджера
    searchValue: (value, row) => {
      if (!row.manager) return '';
      return row.manager.full_name || row.manager.email || '';
    },
  },
  {
    key: 'partnership_status',
    label: 'Статус',
    sortable: true,
    filterable: true,
    filterOptions: partnershipFilterOptions,
    searchable: true,
    // Поиск по отображаемому названию статуса
    searchValue: (value) => {
      const badge = statusesMap[value];
      return badge ? badge.label : value;
    },
  },
];


/**
 * Форматирование даты
 */
function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow'
  });
}

/**
 * Основной компонент таблицы организаций
 */
export function OrganizationsTable({
  data,
  filters,
  onFiltersChange,
  statusesMap: externalStatusesMap,
  // Серверный режим
  filterOptions,
  onSearchChange,
  onSortChange,
  searchValue,
}) {
  const navigate = useNavigate();

  // Используем переданный маппинг или фолбэк
  const statusesMap = externalStatusesMap || FALLBACK_PARTNERSHIP_BADGES;

  // Строим опции фильтра: из переданного маппинга или фолбэк
  const partnershipFilterOptions = externalStatusesMap
    ? Object.entries(externalStatusesMap).map(([value, { label }]) => ({ value, label }))
    : FALLBACK_PARTNERSHIP_FILTER_OPTIONS;

  const columns = getColumns(statusesMap, partnershipFilterOptions, filterOptions);

  const handleRowClick = (row) => {
    navigate(`/organizations/${row.id}`);
  };

  const renderCell = (row, column) => {
    switch (column.key) {
      case 'name':
        return (
          <>
            <Group gap={6} wrap="nowrap">
              {row.is_company_group && (
                <Tooltip label="Группа компаний" withArrow>
                  <IconBuilding
                    size={16}
                    color={tokens.colors.brand.primary}
                    style={{ flexShrink: 0 }}
                  />
                </Tooltip>
              )}
              <Text fw={500} fz="sm" lineClamp={1}>
                {row.name}
              </Text>
            </Group>
            {row.address && (
              <Text fz="xs" c="dimmed" lineClamp={1}>
                {row.address}
              </Text>
            )}
          </>
        );

      case 'organization_type': {
        if (!row.organization_type) return <Text fz="sm" c="dimmed">—</Text>;
        const typeConfig = {
          b2b: { color: 'blue', label: 'B2B' },
          b2c: { color: 'green', label: 'B2C' },
          individual: { color: 'orange', label: 'Инд.' },
        };
        const typeInfo = typeConfig[row.organization_type] || { color: 'gray', label: row.organization_type };
        return (
          <Badge color={typeInfo.color} variant="filled" size="sm" radius="xl">
            {typeInfo.label}
          </Badge>
        );
      }

      case 'company_group_name':
        if (!row.company_group_name) return <Text fz="sm" c="dimmed">—</Text>;
        return (
          <Text fz="sm" lineClamp={1}>
            {row.company_group_name}
          </Text>
        );

      case 'rating':
        if (!row.rating) return <Text fz="sm" c="dimmed">—</Text>;
        return (
          <Group gap={4} wrap="nowrap">
            <Text fw={600} fz="sm">{row.rating}</Text>
            <Text fz="xs" c="dimmed">({row.review_count || 0})</Text>
          </Group>
        );

      case 'partnership_status':
        const badge = statusesMap[row.partnership_status] || {
          color: 'gray',
          label: row.partnership_status || 'Не указан'
        };
        return (
          <Badge color={badge.color} variant="filled" size="sm" radius="xl">
            {badge.label}
          </Badge>
        );

      case 'cooperation_stage':
        const stageBadge = STAGE_BADGES[row.cooperation_stage] || {
          color: '#999999',
          label: row.cooperation_stage || '—'
        };
        return (
          <Badge
            variant="filled"
            size="sm"
            radius="xl"
            styles={{
              root: {
                backgroundColor: stageBadge.color,
                color: 'white',
              }
            }}
          >
            {stageBadge.label}
          </Badge>
        );

      // Для фильтрации используем assigned_to_user_id, но отображаем assigned_to
      case 'assigned_to_user_id':
        if (!row.assigned_to) return <Text fz="sm" c="dimmed">—</Text>;
        return (
          <Text fz="sm" lineClamp={1}>
            {row.assigned_to.full_name || row.assigned_to.email || '—'}
          </Text>
        );

      // Для фильтрации используем manager_user_id, но отображаем manager
      case 'manager_user_id':
        if (!row.manager) return <Text fz="sm" c="dimmed">—</Text>;
        return (
          <Text fz="sm" lineClamp={1}>
            {row.manager.full_name || row.manager.email || '—'}
          </Text>
        );

      case 'created_at':
        return <Text fz="sm">{formatDate(row.created_at)}</Text>;

      case 'district_name':
        return <Text fz="sm">{row.district_name || '—'}</Text>;

      default:
        return undefined; // Использовать дефолтный рендеринг
    }
  };

  return (
    <DataTable
      columns={columns}
      data={data}
      onRowClick={handleRowClick}
      renderCell={renderCell}
      sortable={true}
      filters={filters}
      onFiltersChange={onFiltersChange}
      skipProcessing={!!onSearchChange}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      searchValue={searchValue}
    />
  );
}

export default OrganizationsTable;
